import { Star } from "@nova/component-icons"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    selectActiveWorkspaceId,
    setIdRenaming,
    updateTag,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { WorkspacePanelEmptySectionMessage } from "./WorkspacePanelEmptySectionMessage"
import { WorkspaceProjectTab } from "../tab/WorkspaceProjectTab"
import { WorkspaceFolderTab } from "../tab/WorkspaceFolderTab"
import { selectProjectsByTagFilter, selectAllFires } from "features/entity-caching/fires/selectors"
import { Ref, useCallback, useMemo, useRef } from "react"
import { ID } from "@nova/constants"
import { WorkspaceDragItem, WorkspaceEntityType } from "features/data-management/workspace/types"
import { isDraggingWorkspaceEntity } from "features/data-management/workspace/module"
import { useTranslation } from "@nova/use-translation"
import { useDrop } from "react-dnd"
import classNames from "classnames"
import * as Icons from "@nova/component-icons"
import { NovaRouteTree, useRouter } from "pages"
import { useLocation } from "react-router"
import { GetScopedTo } from "@nova/react-router"
import {
    selectAllFoldersById,
    selectSortedSidePanelRootFolders,
    selectFavoritesData,
} from "features/data-management/workspace/context/selectors"
import {
    toggleFavorite,
    moveWorkspaceEntity,
    deleteWorkspaceEntity,
    openWorkspaceShareModal,
    overwriteFavorites,
} from "features/data-management/workspace/context/thunks"
import { updateFire } from "features/entity-caching/fires/thunks"
import {
    useActivateProject,
    useCreateFolder,
} from "features/data-management/workspace/context/navigation"

export const WorkspacePanelProjectsAndFolders = () => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()

    const workspaceId = useAppSelector(selectActiveWorkspaceId())
    const allFoldersById = useAppSelector(selectAllFoldersById)
    const allProjectsById = useAppSelector(selectAllFires)
    const sortedSidePanelRootFolders = useAppSelector(selectSortedSidePanelRootFolders)
    const { sortedFavoriteIds, favoriteIds } = useAppSelector(selectFavoritesData)
    const isFavorite = (id: string) => favoriteIds.has(id)
    const activateProject = useActivateProject()
    const createFolder = useCreateFolder()

    const handleToggleFavorite = (id: string) => dispatch(toggleFavorite(id))
    const handleMove = (id: string, type: WorkspaceEntityType, newParentFolderId?: string) =>
        dispatch(moveWorkspaceEntity({ id, type, newParentFolderId }))
    const handleDelete = (id: string, name: string, type: WorkspaceEntityType) =>
        dispatch(deleteWorkspaceEntity({ id, name, type }))
    const handleOpenShareModal = (id: string) => dispatch(openWorkspaceShareModal(id))
    const handleBeginRenaming = (id: string) => dispatch(setIdRenaming(id))
    const handleConfirmRenaming = (id: string, name: string, type: WorkspaceEntityType) => {
        if (type === WorkspaceEntityType.Folder) {
            dispatch(updateTag({ id, updates: { name } }))
        } else {
            dispatch(updateFire({ id, updates: { name } }))
        }
        dispatch(setIdRenaming(undefined))
    }
    const handleCancelRenaming = () => dispatch(setIdRenaming(undefined))

    const rootLevelProjects = useAppSelector(selectProjectsByTagFilter([]))

    const rootLevelFolderIds = useMemo(
        () => sortedSidePanelRootFolders?.map((folder) => folder.id) || [],
        [sortedSidePanelRootFolders]
    )

    const rootLevelProjectIds = useMemo(
        () => rootLevelProjects.map((project) => project.id),
        [rootLevelProjects]
    )

    const [{ isOver, anythingIsDragging, draggedId }, dropRef] = useDrop(
        () => ({
            accept: [WorkspaceEntityType.Folder, WorkspaceEntityType.Project],
            drop: (item: WorkspaceDragItem) => handleToggleFavorite(item.id),
            collect: (monitor) => ({
                isOver: monitor.isOver(),
                anythingIsDragging: isDraggingWorkspaceEntity(monitor),
                draggedId: monitor.getItem<WorkspaceDragItem | null>()?.id,
            }),
        }),
        []
    )
    const favoritesDropRef = useCallback(
        (node: HTMLDivElement | null) => {
            dropRef(node)
        },
        [dropRef]
    )

    if (!workspaceId) return null

    return (
        <>
            <div>
                {anythingIsDragging && sortedFavoriteIds.length === 0 ? (
                    <div
                        ref={favoritesDropRef}
                        className={classNames(
                            "w-full h-[4.5rem] mb-2 rounded-lg border border-blue-500 flex items-center justify-center",
                            { "border-dashed": !isOver }
                        )}
                    >
                        <h5 className="text-gray-500 select-none">
                            {t("Drop to add to Favorites")}
                        </h5>
                    </div>
                ) : (
                    <WorkspaceExplorerNestedSidebarSectionHeader
                        to={{ path: "/explore/favorites" }}
                        title="Favorites"
                        className="border-transparent"
                    />
                )}

                <div className={classNames("relative", { "mt-1": sortedFavoriteIds.length > 0 })}>
                    {anythingIsDragging && sortedFavoriteIds.length > 0 && (
                        <div className="absolute -inset-1 rounded-lg border border-dashed border-blue-500 pointer-events-none" />
                    )}
                    {sortedFavoriteIds.map((id) => {
                        const key = `fav_${id}`
                        const folder = allFoldersById[id]
                        const project = allProjectsById[id]

                        return (
                            <div key={key} className="relative">
                                {folder ? (
                                    <WorkspaceFolderTab
                                        folder={folder}
                                        categoryKey={key}
                                        isInFavorites
                                        tagChain={[folder.id]}
                                        isFavorite={isFavorite}
                                        handleMove={handleMove}
                                        handleCreateFolder={(insideOfId) =>
                                            createFolder({ insideOfId })
                                        }
                                        handleBeginRenaming={handleBeginRenaming}
                                        handleConfirmRenaming={handleConfirmRenaming}
                                        handleCancelRenaming={handleCancelRenaming}
                                        handleOpenShareModal={handleOpenShareModal}
                                        handleToggleFavorite={(id) =>
                                            handleToggleFavorite(id).unwrap()
                                        }
                                        handleDelete={handleDelete}
                                        subFolders={folder.subTags}
                                        shouldPopulateChildren={false}
                                        depth={0}
                                    />
                                ) : project ? (
                                    <WorkspaceProjectTab
                                        project={project}
                                        categoryKey={key}
                                        isInFavorites
                                        isFavorite={isFavorite(id)}
                                        handleActivateProject={activateProject}
                                        handleMove={handleMove}
                                        handleBeginRenaming={handleBeginRenaming}
                                        handleConfirmRenaming={handleConfirmRenaming}
                                        handleCancelRenaming={handleCancelRenaming}
                                        handleOpenShareModal={handleOpenShareModal}
                                        handleToggleFavorite={(id) =>
                                            handleToggleFavorite(id).unwrap()
                                        }
                                        handleDelete={handleDelete}
                                    />
                                ) : null}
                                {anythingIsDragging && draggedId !== id && (
                                    <>
                                        <FavoriteReorderZone
                                            id={id}
                                            sortedFavoriteIds={sortedFavoriteIds}
                                            edge="top"
                                            isFolder={!!folder}
                                        />
                                        <FavoriteReorderZone
                                            id={id}
                                            sortedFavoriteIds={sortedFavoriteIds}
                                            edge="bottom"
                                            isFolder={!!folder}
                                        />
                                    </>
                                )}
                            </div>
                        )
                    })}
                </div>

                {sortedFavoriteIds.length === 0 && !anythingIsDragging && (
                    <WorkspacePanelEmptySectionMessage title="To add to the side bar" Icon={Star} />
                )}
            </div>

            <div>
                <DroppableSidebarSectionHeader
                    to={{ path: "/explore/ws" }}
                    title="Workspace"
                    draggedItemCanDrop={(item) =>
                        !item.isFromFavorites &&
                        !rootLevelFolderIds.includes(item.id) &&
                        !rootLevelProjectIds.includes(item.id)
                    }
                    handleDrop={({ id, type }) => handleMove(id, type)}
                />
                {sortedSidePanelRootFolders.map((folder) => {
                    const key = `all_${folder.id}`
                    return (
                        <WorkspaceFolderTab
                            key={key}
                            folder={folder}
                            categoryKey={key}
                            isInFavorites={false}
                            tagChain={[folder.id]}
                            isFavorite={isFavorite}
                            handleMove={handleMove}
                            handleCreateFolder={(insideOfId) => createFolder({ insideOfId })}
                            handleBeginRenaming={handleBeginRenaming}
                            handleConfirmRenaming={handleConfirmRenaming}
                            handleCancelRenaming={handleCancelRenaming}
                            handleOpenShareModal={handleOpenShareModal}
                            handleToggleFavorite={(id) => handleToggleFavorite(id).unwrap()}
                            handleDelete={handleDelete}
                            subFolders={folder.subTags}
                            shouldPopulateChildren={true}
                            depth={0}
                        />
                    )
                })}
            </div>
        </>
    )
}

const FavoriteReorderZone = ({
    id,
    sortedFavoriteIds,
    edge,
    isFolder,
}: {
    id: ID
    sortedFavoriteIds: ID[]
    edge: "top" | "bottom"
    isFolder: boolean
}) => {
    const dispatch = useAppDispatch()
    const [{ isActive }, dropRef] = useDrop(
        () => ({
            accept: [WorkspaceEntityType.Folder, WorkspaceEntityType.Project],
            drop: (item: WorkspaceDragItem) => {
                const reordered = sortedFavoriteIds.filter((favoriteId) => favoriteId !== item.id)
                reordered.splice(reordered.indexOf(id) + (edge === "bottom" ? 1 : 0), 0, item.id)
                dispatch(overwriteFavorites(reordered))
            },
            canDrop: (item) => item.id !== id,
            collect: (monitor) => ({
                isActive: monitor.isOver() && monitor.canDrop(),
            }),
        }),
        [sortedFavoriteIds, dispatch, id, edge]
    )
    const reorderDropRef = useRef<HTMLDivElement>(null)
    dropRef(reorderDropRef)
    return (
        <div
            ref={reorderDropRef}
            className={classNames("absolute inset-x-0 z-50 flex px-4", {
                "top-0 items-start": edge === "top",
                "bottom-0 items-end": edge === "bottom",
                "h-1/4": isFolder,
                "h-1/2": !isFolder,
            })}
        >
            <div
                className={classNames("w-full h-px", {
                    "-translate-y-px": edge === "top",
                    "bg-blue-500": isActive,
                })}
            />
        </div>
    )
}

type SidebarSectionHeaderLinkProps = {
    title: string
    to: GetScopedTo<NovaRouteTree, "/workspace/:workspaceId">
}

const DroppableSidebarSectionHeader = ({
    title,
    to,
    draggedItemCanDrop,
    handleDrop,
}: SidebarSectionHeaderLinkProps & {
    draggedItemCanDrop: (item: WorkspaceDragItem) => boolean
    handleDrop: (item: WorkspaceDragItem) => void
}) => {
    const [{ isOver, canDrop, anythingIsDragging }, dropRef] = useDrop(
        () => ({
            accept: [WorkspaceEntityType.Folder, WorkspaceEntityType.Project],
            drop: handleDrop,
            collect: (monitor) => ({
                isOver: !!monitor.isOver(),
                canDrop: !!monitor.canDrop(),
                anythingIsDragging: isDraggingWorkspaceEntity(monitor),
            }),
            canDrop: draggedItemCanDrop,
        }),
        [draggedItemCanDrop, handleDrop]
    )
    const headerDropRef = useRef<HTMLDivElement>(null)
    dropRef(headerDropRef)
    return (
        <WorkspaceExplorerNestedSidebarSectionHeader
            ref={headerDropRef}
            title={title}
            to={to}
            className={classNames({
                "border-transparent": !isOver,
                "border-blue-500": isOver && canDrop,
                "border-red-500": isOver && !canDrop,
                "opacity-20": anythingIsDragging && !canDrop,
            })}
        />
    )
}

const WorkspaceExplorerNestedSidebarSectionHeader = ({
    title,
    to,
    className,
    ref,
}: SidebarSectionHeaderLinkProps & {
    className: string
    ref?: Ref<HTMLDivElement>
}) => {
    const router = useRouter("/workspace/:workspaceId")
    const url = router.preview(to)
    const location = useLocation()
    const isActive = useMemo(() => url === location.pathname, [location.pathname, url])
    return (
        <router.Link to={to}>
            <div
                ref={ref}
                className={classNames(
                    "group relative w-full p-2 rounded-lg border transition-colors flex justify-between items-center",
                    className,
                    {
                        "hover:bg-white-100": !isActive,
                        "bg-white-200": isActive,
                    }
                )}
            >
                <div
                    className={classNames("transition-colors", {
                        "text-gray-100 group-hover:text-white": !isActive,
                        "text-white": isActive,
                    })}
                >
                    {title}
                </div>
                <Icons.ExtraSmallIcon
                    Icon={Icons.ArrowRight}
                    iconClassName={classNames("opacity-0 transition-opacity text-blue-500", {
                        "group-hover:opacity-100": !isActive,
                    })}
                />
            </div>
        </router.Link>
    )
}
